import { useState } from 'react';
import { useOnboardingStore } from '@/store/onboardingStore';
import { TextureButton } from '@/components/ui/TextureButton';
import { Upload, ScanFace } from 'lucide-react';

export function VideoAnalysisScreen() {
    const { nextStep } = useOnboardingStore();
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('Initializing AI...');

    const handleUpload = () => {
        setUploading(true);

        // Simulated Analysis Flow
        const stages = [
            { pct: 10, text: 'Uploading match footage...' },
            { pct: 30, text: 'Mapping body positioning...' },
            { pct: 56, text: 'Evaluating takedown angles...' },
            { pct: 82, text: 'Identifying scoring sequences...' },
            { pct: 100, text: 'Analysis Complete.' }
        ];

        let i = 0;
        const interval = setInterval(() => {
            if (i >= stages.length) {
                clearInterval(interval);
                setTimeout(nextStep, 500);
                return;
            }
            const stage = stages[i];
            if (stage) {
                setProgress(stage.pct);
                setStatus(stage.text);
            }
            i++;
        }, 800);
    };

    if (uploading) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] animate-fade-in space-y-8">
                <div className="relative w-48 h-48 flex items-center justify-center">
                    <div className="absolute inset-0 border-4 border-white/10 rounded-full" />
                    <div
                        className="absolute inset-0 border-4 border-gold-500 rounded-full border-t-transparent animate-spin"
                        style={{ clipPath: `inset(0 ${100 - progress}% 0 0)` }}
                    />
                    {/* Modern Scan Effect */}
                    <div className="absolute inset-0 rounded-full border border-gold-500/30 animate-ping opacity-20" />

                    <div className="text-4xl font-black text-white">{progress}%</div>
                </div>

                <div className="text-center space-y-2">
                    <h3 className="text-xl font-bold text-white animate-pulse">{status}</h3>
                    <p className="text-text-muted text-xs">Do not close this window</p>
                </div>
            </div>
        );
    }

    return (
        <div className="px-2 animate-fade-in-right space-y-6">
            <h2 className="text-xl font-bold text-text-primary">Video Analysis</h2>

            <div className="bg-surface-card border border-white/10 rounded-2xl p-6 text-center space-y-4">
                <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mx-auto">
                    <ScanFace className="text-white w-6 h-6" />
                </div>
                <div>
                    <h3 className="font-bold text-white text-lg">Submit Your Latest Match</h3>
                    <p className="text-text-secondary text-xs mt-1">So we can finalize your training program</p>
                </div>

                <button
                    onClick={handleUpload}
                    className="bg-white text-bg font-bold px-6 py-2 rounded-full hover:bg-gray-200 transition-colors text-sm"
                >
                    Select Video
                </button>
            </div>

            <div className="bg-surface-card border border-white/10 rounded-2xl p-6 space-y-4">
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-gold-500">✨</span>
                    <h4 className="font-bold text-white text-sm">How It Works</h4>
                </div>

                {[
                    { title: 'Upload', desc: 'Select your wrestling footage' },
                    { title: 'AI Analysis', desc: 'Frame-by-frame breakdown' },
                    { title: 'Get Insights', desc: 'Personalized recommendations' }
                ].map((step, idx) => (
                    <div key={idx} className="flex gap-4 relative">
                        {/* Connector Line */}
                        {idx !== 2 && <div className="absolute left-[11px] top-8 bottom-[-16px] w-[2px] bg-white/10" />}

                        <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold shrink-0 border border-blue-500/30">
                            {idx + 1}
                        </div>
                        <div>
                            <div className="text-white font-bold text-sm">{step.title}</div>
                            <div className="text-text-muted text-xs">{step.desc}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 space-y-4">
                <TextureButton onClick={handleUpload} className="w-full gap-2">
                    <Upload className="w-4 h-4" />
                    Upload Video
                </TextureButton>

                <button
                    onClick={nextStep}
                    className="w-full text-center text-xs font-bold text-text-muted hover:text-white transition-colors"
                >
                    Skip for now
                </button>
            </div>
        </div>
    );
}
